import { isNil } from 'lodash';
import {
  largeCurrencyFormat,
  largeDecimalFormat,
  percentFormat,
  standardDecimalFormat,
  twoDecimalPercentFormat,
  xStandardSuffixFormat,
} from 'common/formats/formats';
import type { Styling } from 'common/types/dashboard/dashboardDefinition';
import { getValueType } from 'common/types/summaryDefinitions';
import { formatCompactCurrency, formatNumbers } from 'utilities';

export type NumberFormat = NonNullable<Styling['number_format']>;

// Same descriptors the firm summary spreadsheet uses for the same columns (see createParser), so a
// dashboard card and a table cell never disagree about how a metric is written.
const FORMAT_DESCRIPTORS = {
  currency: largeCurrencyFormat,
  decimal: standardDecimalFormat,
  integer: largeDecimalFormat,
  multiplier: xStandardSuffixFormat,
  percent: twoDecimalPercentFormat({}),
} as const;

// One decimal, negatives allowed — the "↑ +12.4%" line in the mockups.
const RELATIVE_CHANGE_FORMAT = percentFormat({ fractionDigits: 1, isDecimal: true });

/**
 * Fall back to the metric's own declared format when the AI omitted `number_format`.
 *
 * Two traps live here. `DEFAULT_DISPLAY_FORMATS.moic` is `{type:'number', decimals:2,
 * thousandsSeparator:false}` with no "x" hint at all, so `thousandsSeparator === false` is the only
 * signal that a number is a multiple — without that branch MOIC renders "1.84" instead of "1.84x".
 * And `percentage.multiplier: 100` means "the stored value is a fraction, scale it", which is
 * `formatNumbers`' `isDecimal: true`; reading it the other way is a 100x error.
 */
const formatFromMetric = (metric: string): NumberFormat => {
  const displayFormat = getValueType(metric)?.displayFormat;

  if (!displayFormat) {
    return 'decimal';
  }

  if (displayFormat.type === 'currency') {
    // Compact even when the registry entry is not, because the registry describes spreadsheet
    // cells, where full precision is right. A headline tile is the opposite case: "$127,000,000"
    // dominates the card and is harder to read at a glance than "$127M". An explicit
    // `number_format: 'currency'` from the AI still wins — this is only the fallback.
    return 'currency_compact';
  }
  if (displayFormat.type === 'percentage') {
    return 'percent';
  }
  if (displayFormat.type === 'number') {
    if (displayFormat.thousandsSeparator === false) {
      return 'multiplier';
    }
    return displayFormat.decimals === 0 ? 'integer' : 'decimal';
  }

  return 'decimal';
};

interface ResolveFormatParams {
  metric: string;
  styling?: Styling;
  // styling.number_format describes the whole component, so it can only be trusted when the card
  // shows a single metric. A card mixing total_value and moic would otherwise print "$1.84".
  isSingleMetric: boolean;
}

export const resolveNumberFormat = ({
  metric,
  styling,
  isSingleMetric,
}: ResolveFormatParams): NumberFormat => {
  if (isSingleMetric && styling?.number_format) {
    return styling.number_format;
  }
  return formatFromMetric(metric);
};

interface FormatValueParams {
  value: number;
  format: NumberFormat;
  currencyCode?: string;
}

type FormatDescriptor = (typeof FORMAT_DESCRIPTORS)[keyof typeof FORMAT_DESCRIPTORS];

// formatNumbers declares every parameter as required even though it treats them all as optional,
// so every call site in the app suppresses it. Keeping the single suppression here stops it
// spreading into the components.
//
// `currencyCode` is passed rather than `currency`, deliberately: the `currency` object carries a
// `units` field that divides the value by a thousands/millions divisor, which would scale a
// dashboard headline figure by 10^6.
const applyFormat = (
  format: FormatDescriptor,
  value: number,
  currencyCode: string
): string =>
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: formatNumbers marks all params required but treats them as optional
  formatNumbers({
    // allowNegativeValue matters on every path, not just deltas: getPercentValue clamps a negative
    // to +0.001 without it, so a -8% margin would silently render as 0.1%.
    allowNegativeValue: true,
    currencyCode,
    format,
    value,
  });

export const formatMetricValue = ({
  value,
  format,
  currencyCode = 'USD',
}: FormatValueParams): string => {
  if (format === 'currency_compact') {
    return formatCompactCurrency({ currencyCode, value });
  }

  return applyFormat(FORMAT_DESCRIPTORS[format], value, currencyCode);
};

const withSign = (value: number, formatted: string): string =>
  value > 0 ? `+${formatted}` : formatted;

/** The absolute change, in the metric's own unit: `+$14M`, `-$14M`, `+0.16x`. */
export const formatAbsoluteChange = ({
  value,
  format,
  currencyCode = 'USD',
}: FormatValueParams): string =>
  withSign(value, formatMetricValue({ currencyCode, format, value }));

/** The relative change, always a signed one-decimal percent: `+12.4%`. */
export const formatRelativeChange = (fraction: number): string =>
  withSign(fraction, applyFormat(RELATIVE_CHANGE_FORMAT, fraction, 'USD'));

/**
 * Whether a relative-change line belongs beside the absolute one.
 *
 * A currency card shows both ("+$14M vs prior period" and "↑ +12.4%"). A multiplier or percent
 * card shows only the absolute change: "IRR rose 12.4%" is ambiguous between percentage points and
 * a relative move, and one honest number beats two confusable ones.
 */
export const shouldShowRelativeChange = (format: NumberFormat): boolean =>
  format !== 'multiplier' && format !== 'percent';

const COMPARISON_CAPTIONS = {
  entry: 'vs entry',
  prior_period: 'vs prior period',
} as const;

export const comparisonCaption = (mode: keyof typeof COMPARISON_CAPTIONS): string =>
  COMPARISON_CAPTIONS[mode];

/**
 * Label for one metric on a card.
 *
 * `display.title` names the card, so it is only the metric's label when the card shows exactly one
 * metric. Otherwise the registry supplies it — the same source the spreadsheet column headers use,
 * so both surfaces name a metric identically.
 */
interface ResolveLabelParams {
  metric: string;
  fallbackLabel?: string;
  title?: string;
  isSingleMetric: boolean;
}

export const resolveMetricLabel = ({
  metric,
  fallbackLabel,
  title,
  isSingleMetric,
}: ResolveLabelParams): string => {
  if (isSingleMetric && title?.trim()) {
    return title;
  }
  return getValueType(metric)?.displayName ?? fallbackLabel ?? metric;
};

/** 0 is a valid total, so presence is tested with isNil rather than truthiness. */
export const hasValue = (value: number | null | undefined): value is number => !isNil(value);
